import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import Reveal from "@/components/shared/Reveal";
import { useSettings } from "@/components/layout/PublicLayout";
import Seo from "@/components/shared/Seo";
import { ArrowRight } from "lucide-react";

const Archives = () => {
  const settings = useSettings();
  const tx = settings?.pages?.archives || {};
  const { data: editions = [], isLoading } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const past = editions.filter((e) => e.status !== "upcoming").sort((a, b) => b.year - a.year);
  const upcoming = editions.filter((e) => e.status === "upcoming");

  return (
    <>
      <Seo title={tx.seo_title || "Archives des éditions — FACADES2build"} description={tx.seo_description || "Toutes les éditions de FACADES2build : Paris, Lyon, et les archives éditoriales."} />
      <section className="bg-f2b-black pt-40 pb-16">
        <div className="f2b-container">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-4">{tx.kicker || "Toutes les éditions"}</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display font-bold text-white text-4xl md:text-6xl leading-tight max-w-3xl">{tx.title || "Archives des éditions"}</h1></Reveal>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="f2b-container space-y-6">
          {isLoading ? <p className="text-f2b-gray">Chargement…</p> : past.map((ed, i) => (
            <Reveal key={ed.id} delay={i * 0.05}>
              <Link to={`/archives/${ed.slug}`} className="group grid md:grid-cols-[320px_1fr] gap-8 items-center border-b border-f2b-filet pb-6" data-testid={`archive-${ed.year}`}>
                <div className="aspect-[16/10] overflow-hidden bg-f2b-light">
                  {mediaUrl(ed.hero_image) ? <img src={mediaUrl(ed.hero_image)} alt={`Édition ${ed.year}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full bg-f2b-black flex items-center justify-center"><span className="font-display font-bold text-white text-4xl">{ed.year}</span></div>}
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-display font-bold text-f2b-cyan text-2xl">{ed.date_label}</span>
                    <span className="text-f2b-gray text-sm">· {ed.city}</span>
                    {ed.is_demo && <span className="text-xs bg-f2b-gold/15 text-f2b-gold px-2 py-0.5">Donnée de démonstration</span>}
                  </div>
                  <h2 className="font-display font-bold text-f2b-black text-2xl md:text-3xl leading-tight group-hover:text-f2b-cyan transition-colors">{ed.theme}</h2>
                  <p className="text-f2b-text/70 mt-3 line-clamp-2 max-w-2xl">{ed.summary}</p>
                  <span className="inline-flex items-center gap-2 mt-4 text-sm text-f2b-black f2b-link-underline">Découvrir <ArrowRight size={16} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {upcoming.length > 0 && (
        <section className="py-16 bg-f2b-cyan">
          <div className="f2b-container flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="f2b-kicker text-f2b-black/70 mb-2">Prochaine édition</p>
              <h2 className="font-display font-bold text-f2b-black text-3xl md:text-4xl">{upcoming[0].date_label}</h2>
            </div>
            <Link to="/edition-2028" className="inline-flex items-center gap-2 bg-f2b-black text-white px-7 py-3.5 text-sm hover:-translate-y-0.5 transition-transform">Être informé <ArrowRight size={18} /></Link>
          </div>
        </section>
      )}
    </>
  );
};

export default Archives;
